import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIsMobile } from '@/hooks/use-mobile';
import { DialogFooter } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2 } from 'lucide-react'; // ← Import du loader

interface Location {
  id: string;
  name: string;
}

interface EditLocationFormProps {
  location: Location;
  onSubmit: (location: Location) => void;
  onCancel: () => void;
}

const EditLocationForm: React.FC<EditLocationFormProps> = ({ location, onSubmit, onCancel }) => {
  const [name, setName] = useState(location.name);
  const [isSubmitting, setIsSubmitting] = useState(false); // ← État du chargement
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      setIsSubmitting(true); // ← Activation du loading
      try {
        await onSubmit({
          ...location,
          name: name.trim()
        });
      } finally {
        setIsSubmitting(false); // ← Désactivation du loading
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`${!isMobile ? 'h-auto' : 'h-full'} flex flex-col`}>
      <div className={`flex-grow overflow-auto ${!isMobile ? 'max-h-[60vh] pb-5' : 'p-5'}`}>
        <Label htmlFor="edit-location-name">{t('locations.name')}</Label>
        <Input
          id="edit-location-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter location name"
          autoFocus
        />
      </div>
      <DialogFooter className={isMobile ? "px-6 py-4 border-t sticky bottom-0 bg-gray-50 z-20" : "pt-5"}>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('common.processing')}
            </span>
          ) : (
            t('common.save_change')
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default EditLocationForm;
